/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import {createRef, PureComponent, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { DeviceType } from 'Type/Device';
import './StorePage.style';
import media, { WYSIWYG_MEDIA} from "Util/Media/Media";
import { STORE_LOCATOR_MEDIA_STORE } from "../../utils/media/index";
import Loader from 'Component/Loader/Loader.component';
import Link from "Component/Link";
import Html from 'Component/Html';

export const GoogleMap = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "store-map" */
    '../../component/GoogleMap'
    ));

export default class StorePage extends PureComponent {
    static propTypes = {
        setActiveTab : PropTypes.func.isRequired,
        store : PropTypes.object,
        currentCategory : PropTypes.object,
        device: DeviceType.isRequired
    };

    static defaultProps = {
    };

    renderStoreTitle(){
        const { store } = this.props;

        if(!store)
            return ;
        return <h1 className="StorePage-title">{store.name}</h1>
    }

    renderStoreDescription(){
        const { store } = this.props;

        if(!store)
            return ;
        return <div className="StorePage-description">{store.description}</div>
    }

    renderCategoryStores(){
        const { currentCategory : { category_id, name, stores }, store_id } = this.props;
        return(
            <div className="StorePage-category">
                <div className="StorePage-category_current">
                    { __('Nearby shops in the area:') }
                    <strong>{name}</strong>
                </div>
                <div className="StorePage-category_stores">
                    <div className="StorePage-category_stores_wrapper">
                        {
                            stores && Object.values(stores).map(store => {
                                return (
                                    <Link to={store.url_alias} className={store.store_id == store_id ? 'current' : "" }><span>{store.name}</span></Link>
                                );
                            })
                        }
                    </div>
                </div>
            </div>
        );
    }

    renderLocationSlider() {
        const { store: { add_this_location } } = this.props;

        if(!add_this_location) {
            return null;
        }

        return (
            <div className="StorePage-location">
                <Html content={ add_this_location } />
            </div>
        );

    }

    renderImportantNotices() {
        const { store: { important_notices } } = this.props;

        if(!important_notices) {
            return null;
        }

        return (
            <div className="StorePage-store_info_section StorePage-store_info_section_important_info">
                <div className="StorePage-store_info_title">{__('IMPORTANT INFORMATION')}</div>
                <div className="StorePage-store_info_content">
                    <Html content={ important_notices } />
                </div>
            </div>
        );

    }

    renderCategoriesSlider() {
        const { store: { categories } } = this.props;

        if(!categories) {
            return null;
        }

        return (
            <div className="StorePage-categories">
                <Html content={ categories } />
            </div>
        );

    }

    renderBrandSlider() {
        const { store: { brand_slider } } = this.props;

        if(!brand_slider) {
            return null;
        }

        return (
            <div className="StorePage-brand_slider">
                <Html content={ brand_slider } />
            </div>
        );

    }

    renderPopularProductsSlider() {
        const { store: { popular_products } } = this.props;

        if(!popular_products) {
            return null;
        }

        return (
            <div className="StorePage-popular_products">
                <Html content={ popular_products } />
            </div>
        );

    }

    renderBannerSlider(){
        const { store : { banner_slider } } = this.props;
        if(!banner_slider)
            return null;
        return <Html content={banner_slider} />;

    }

    renderStoreInfo(){
        const { store : { address, postcode, city, country, phone, email, fax, open_hours, name } } = this.props;
        return (
            <div className="StorePage-store_info">
                <div className="StorePage-store_info_col">
                    <div className="StorePage-store_info_section">
                        <div className="StorePage-store_info_title">{__('LOCATION')}</div>
                        <div className="StorePage-store_info_content">
                            { __('Hondos Center: %s', name) }<br/>
                            { `${address} ${city}, ${postcode}` }
                        </div>
                    </div>
                    { this.renderImportantNotices() }
                </div>
                <div className="StorePage-store_info_col">
                    { email &&
                        <div className="StorePage-store_info_section StorePage-store_info_section_contact">
                            <div className="StorePage-store_info_title">{__('CONTACT')}</div>
                            <div className="StorePage-store_info_content">
                                { __('email: ') }
                                <a href={ `mailto:${email} ` }>{ email }</a><br/>
                                { phone && <a href={ `tel:${phone} ` } className="StorePage-store_info_phone Button">{phone}</a> }
                            </div>
                        </div>
                    }
                    { open_hours &&
                        <div className="StorePage-store_info_section StorePage-store_info_section_store_hours">
                            <div className="StorePage-store_info_title">{ __('SCHEDULE') }</div>
                            <div className="StorePage-store_info_content" dangerouslySetInnerHTML={{__html: open_hours}}></div>
                        </div>
                    }
                </div>
            </div>
        );
    }

    renderStoreMap(){
        const { storePageData, store, map_markericon, map_selected_markericon} = this.props;

        return(
            <Suspense fallback={ <Loader /> }>
                <GoogleMap
                    items={[store]}
                    selectStore={()=>{}}
                    prevSelectedStores={null}
                    selectedStore={null}
                    addMarkerRef={()=>{}}
                    map_markericon={map_markericon}
                    map_selected_markericon={map_selected_markericon}
                />
            </Suspense>
        );
    }

    renderFloorWidget(){
        const { store : { section_by_floor } } = this.props;
        if(!section_by_floor)
            return null;
        return <Html content={section_by_floor} />;
    }

    renderInfoTabs(){
        const { setActiveTab, activeTab, device, store : { section_by_floor } } = this.props;

        if(device.isMobile) {
            return(
                <div className="StorePage-tabs">
                    <div className='StorePage-tabs_header'>
                        <div className={ `StorePage-tab_header ${activeTab === 1 ? 'active': ''} ` } onClick={()=>setActiveTab(1)} >{__('STORE INFORMATION')}</div>
                        <div className={ `StorePage-tab_content info_tab ${activeTab === 1 ? 'active': ''} ` } >{this.renderStoreInfo()}</div>
                        <div className={ `StorePage-tab_header ${activeTab === 2 ? 'active': ''} ` } onClick={()=>setActiveTab(2)} >{__('MAP')}</div>
                        <div className={ `StorePage-tab_content map_tab ${activeTab === 2 ? 'active': ''} ` } >{ activeTab === 2 && this.renderStoreMap()}</div>
                        { section_by_floor ?
                            <>
                                <div className={ `StorePage-tab_header ${activeTab === 3 ? 'active': ''} ` } onClick={()=>setActiveTab(3)} >{__('SECTIONS BY FLOOR')}</div>
                                <div className={ `StorePage-tab_content widget_tab ${activeTab === 3 ? 'active': ''} ` } >{this.renderFloorWidget()}</div>
                            </>
                            : ""
                        }
                    </div>
                </div>
            );
        }

        return(
            <div className="StorePage-tabs">
                <div className='StorePage-tabs_header'>
                    <div className={ `StorePage-tab_header ${activeTab === 1 ? 'active': ''} ` } onClick={()=>setActiveTab(1)} >{__('STORE INFORMATION')}</div>
                    <div className={ `StorePage-tab_header ${activeTab === 2 ? 'active': ''} ` } onClick={()=>setActiveTab(2)} >{__('MAP')}</div>
                    { section_by_floor ?
                        <div className={ `StorePage-tab_header ${activeTab === 3 ? 'active': ''} ` } onClick={()=>setActiveTab(3)} >{__('SECTIONS BY FLOOR')}</div>
                        : ""
                    }
                </div>
                { activeTab === 1 &&
                    <div className='StorePage-tab_content info_tab'>
                        {this.renderStoreInfo()}
                    </div>
                }
                { activeTab === 2 &&
                    <div className='StorePage-tab_content map_tab'>
                        {this.renderStoreMap()}
                    </div>
                }
                { activeTab === 3 && section_by_floor &&
                    <div className='StorePage-tab_content widget_tab'>
                        {this.renderFloorWidget()}
                    </div>
                }
            </div>
        );
    }

    render() {
        return (
            <Suspense fallback={ <Loader /> }>
                <main
                    block="StorePage"
                >
                    <div block="StorePage" elem="Wrapper" className="ContentWrapper">
                        { this.renderStoreTitle() }
                        { this.renderStoreDescription() }
                        { this.renderCategoryStores() }
                        { this.renderBannerSlider() }
                        { this.renderInfoTabs() }
                        { this.renderLocationSlider() }
                        { this.renderPopularProductsSlider() }
                        { this.renderCategoriesSlider() }
                        { this.renderBrandSlider() }
                    </div>
                </main>
            </Suspense>
        );
    }
}
